import styled from "@xstyled/styled-components";
import { useQuery } from "@apollo/client";
import { Link, useParams } from "react-router-dom";

import { usePaymentCheckoutLink } from "../usePaymentCheckoutLink";
import { JobSlotsModal } from "../JobSlotsModal";

import { Button, Heading, Text } from "@otta/design";
import { pxToRem } from "@otta/design-tokens";
import { Icon } from "@otta/icons";
import { CatalogueProductId, ProductQuoteDocument } from "@hire/schema";
import { Loading } from "@otta/shared-components";
import { formatCurrency } from "@hire/util/currencies";

const IconContainer = styled.div`
  display: inline;
  margin-right: sm;
`;

const ButtonContainer = styled.div`
  margin-top: xl;
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 1rem;
`;

const LineItem = styled(Text)<{ hasMargin?: boolean }>`
  display: flex;
  margin: ${({ hasMargin }) => hasMargin && `lg 0`};
`;

const StyledButton = styled(Button)`
  width: 50%;
`;

export const AddAJobPricing = () => {
  const { data, loading } = useQuery(ProductQuoteDocument, {
    variables: {
      quantity: 1,
      productId: CatalogueProductId.JobSlotMonthly,
    },
  });

  const currency = data?.catalogue.product?.quote.currency;
  const subTotal = data?.catalogue.product?.quote.lineItems[0].netSubtotal;

  const { companySlug } = useParams<"companySlug">();
  const { callback, loading: paymentLinkLoading } = usePaymentCheckoutLink(
    `${companySlug}/jobs/create`
  );

  const formattedCurrency =
    subTotal && currency && formatCurrency(subTotal, currency);

  const checkpoints = [
    "Pay today to start hiring immediately.",
    "No commitment, cancel online anytime.",
    "Reuse this job slot for other jobs.",
    "Subscription will continue until explicitly cancelled, which can be done online.",
  ];

  if (loading) {
    return <Loading />;
  }

  return (
    <JobSlotsModal name="pricing" redirectOnClose={`/${companySlug}/jobs`}>
      <Text size={2} bold>
        Buy a job slot
      </Text>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          margin: `${pxToRem(24)} 0`,
        }}
      >
        <Heading size={3}>{formattedCurrency}</Heading>
        <Text bold size={1} style={{ marginLeft: pxToRem(8) }}>
          per month (excluding VAT)
        </Text>
      </div>

      {checkpoints.map((checkpoint, idx) => (
        <LineItem key={idx}>
          <IconContainer>
            <Icon icon="checkmark" size={1.5} />
          </IconContainer>
          {checkpoint}
        </LineItem>
      ))}

      <ButtonContainer>
        <StyledButton
          level="primary"
          disabled={paymentLinkLoading}
          onClick={callback}
        >
          {paymentLinkLoading ? "Loading" : "Continue to payment"}
        </StyledButton>
        <StyledButton
          level="destructive"
          as={Link}
          to="../why-otta"
          state={{ redirect: `../add-a-job-pricing` }}
        >
          Maybe later
        </StyledButton>
      </ButtonContainer>
    </JobSlotsModal>
  );
};
